import type { IObservableArray } from 'mobx';
import { observable } from 'mobx';

import type { TChannelKey, TChannelStats } from '@feathr/blackbox';
import { AggregatedStat, DailyStat } from '@feathr/blackbox';

interface IChannelStats<T> {
  channels?: TChannelStats<T>;
  unfiltered?: Partial<T>;
}

/**
 * Resolves one channel's stats from combined stats, or keeps the combined stats when no channel
 * is given. A channel's stats are its own alone: a stat it leaves out, as the backend leaves out
 * zeros, is missing, which consumers read as zero. Unless verified activity is prioritized, the
 * resolved stats' own `unfiltered` values replace their verified ones.
 *
 * @param stats - The combined stats, with per-channel stats under `channels`
 * @param channelKey - The backend channel key (e.g., 'tradedesk', 'meta'), or undefined for combined
 * @param prioritizeVerifiedActivity - Whether to report only verified activity, leaving out bot opens
 * @param kept - Fields a channel's stats keep from the combined stats, such as a day's metadata
 * @returns The resolved stats
 */
function resolveChannelData<T extends IChannelStats<T>>(
  stats: T,
  channelKey: TChannelKey | undefined,
  prioritizeVerifiedActivity: boolean,
  kept: Partial<T> = {},
): Partial<T> {
  const data: Partial<T> = channelKey ? { ...kept, ...stats.channels?.[channelKey] } : stats;
  return prioritizeVerifiedActivity ? data : { ...data, ...data.unfiltered };
}

/**
 * Resolves channel-specific stats from the aggregated stat.
 * When a channel key is provided (e.g., 'tradedesk' or 'meta'), extracts that channel's
 * stats from the `channels` object and wraps them in a new AggregatedStat.
 * When undefined (Overview tab), returns the original combined stats.
 * Unless verified activity is prioritized, the resolved stats' own `unfiltered` values replace
 * their verified ones.
 *
 * @param stats - The aggregated stat containing combined and per-channel data
 * @param channelKey - The backend channel key (e.g., 'tradedesk', 'meta'), or undefined for combined
 * @param prioritizeVerifiedActivity - Whether to report only verified activity, leaving out bot opens
 * @returns An AggregatedStat with channel-specific or combined data
 */
function resolveChannelAggregatedStats(
  stats: AggregatedStat,
  channelKey: TChannelKey | undefined,
  prioritizeVerifiedActivity = true,
): AggregatedStat {
  if (!channelKey && prioritizeVerifiedActivity) {
    return stats;
  }
  return new AggregatedStat(
    resolveChannelData(stats.toJS(), channelKey, prioritizeVerifiedActivity),
  );
}

/**
 * Resolves channel-specific daily stats from an array of daily stat models.
 * Each day's stats are replaced by its channel's, keeping the day's metadata.
 * Unless verified activity is prioritized, each day's resolved `unfiltered` values replace its
 * verified ones.
 *
 * @param models - Array of daily stat models
 * @param channelKey - The backend channel key, or undefined for combined
 * @param prioritizeVerifiedActivity - Whether to report only verified activity, leaving out bot opens
 * @returns Array of DailyStat models with channel-specific or combined data
 */
function resolveChannelDailyStats(
  models: IObservableArray<DailyStat>,
  channelKey: TChannelKey | undefined,
  prioritizeVerifiedActivity = true,
): IObservableArray<DailyStat> {
  if (!channelKey && prioritizeVerifiedActivity) {
    return models;
  }
  const resolved = models.map(
    (model) =>
      new DailyStat(
        resolveChannelData(model.toJS(), channelKey, prioritizeVerifiedActivity, {
          metadata: model.get('metadata'),
        }),
      ),
  );
  return observable.array(resolved);
}

export { resolveChannelAggregatedStats, resolveChannelDailyStats };
